import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, updateDoc, setDoc, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { PLANS, PLAN_PRICES, CURRENCIES, DEFAULT_POINT_RULES, FREE_POINT_RULES } from '../utils/constants';
import ModalPago from '../components/ModalPago';

const isNativeAndroid = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform()); }
  catch(e) { return false; }
};

const PLAY_PRICES = {
  starter: '$6 USD', grupo: '$9 USD', empresa: '$13 USD',
  grande: '$21 USD', masivo: '$35 USD',
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Inter:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #080810; color: #F5F5F0; font-family: 'Inter', sans-serif; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-6px); }
    40%, 80% { transform: translateX(6px); }
  }
  .inp:focus { outline: none; border-color: #F0B429 !important; box-shadow: 0 0 0 3px rgba(240,180,41,0.1); }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: #F0B429; border-radius: 2px; }
`;

const C = {
  gold: '#F0B429', goldDark: '#C9820A', bg: '#080810',
  card: '#14141f', border: 'rgba(255,255,255,0.08)',
  green: '#06B86A', red: '#E63946', purple: '#7C3AED'
};

export default function CrearGrupoPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser, userData } = useAuth();

  const [selectedPlan, setSelectedPlan] = useState('free');
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);
  const [showModalPago, setShowModalPago] = useState(false);
  const [groupIdCreado, setGroupIdCreado] = useState(null);
  const [currency, setCurrency] = useState('COP');

  const selectedCurrency = CURRENCIES.find(c => c.code === currency);
  const isPayPal = selectedCurrency?.provider === 'paypal';

  const plan = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
  const isFree = plan.id === 'free';

  // Forzar que la página cargue en la parte superior (Paso 1)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function handleCreate() {
    if (!groupName.trim()) { setError('Ponle un nombre a tu grupo.'); return; }
    if (!currentUser) { navigate('/admin-login'); return; }

    setLoading(true);
    setError('');
    try {
      // Se crea con el plan seleccionado y estado 'pending' si es de pago
      const docRef = await addDoc(collection(db, 'groups'), {
        name: groupName.trim(),
        plan: plan.id, 
        maxMembers: plan.maxMembers, 
        adminId: currentUser.uid,
        adminName: userData?.name || currentUser.displayName || '',
        adminEmail: userData?.email || currentUser.email || '',
        status: isFree ? 'active' : 'pending',
        locked: false,
        pointRules: isFree ? FREE_POINT_RULES : DEFAULT_POINT_RULES,
        pointRulesLocked: isFree,
        createdAt: serverTimestamp(),
        currentMembers: 0
      });

      // Asegurar que el usuario tenga doc en Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        await updateDoc(userRef, { role: 'admin', updatedAt: serverTimestamp() });
      } else {
        await setDoc(userRef, {
          uid: currentUser.uid,
          name: currentUser.displayName || '',
          email: currentUser.email || '',
          role: 'admin',
          groupIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // Pedir permiso de notificaciones si aún no se ha pedido
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }

      if (isFree) {
        // Plan gratis → guardar groupId y recargar app para que el contexto se actualice
        localStorage.setItem('activeGroupId', docRef.id);
        sessionStorage.removeItem('splashShown'); // forzar que el contexto recargue sin splash
        setCreated(true);
        setLoading(false);
        setTimeout(() => {
          window.location.replace('/admin');
        }, 2200);
      } else {
        // Plan de pago → abrir modal de pago
        localStorage.setItem('activeGroupId', docRef.id);
        setGroupIdCreado(docRef.id);
        setShowModalPago(true);
        setLoading(false);
      }
    } catch (e) {
      setError('Error creando el grupo. Intenta de nuevo.');
      console.error(e);
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, overflowX: 'hidden' }}>
      <style>{CSS}</style>

      {/* Fondo decorativo */}
      <div style={{ position: 'fixed', top: '15%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'rgba(240,180,41,0.05)', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 520, margin: '0 auto', padding: '20px 16px 60px', animation: 'fadeIn 0.4s ease' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, paddingTop: 12 }}>
          <button onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', color: '#6B6B7B', fontFamily: "'Inter',sans-serif", fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Volver
          </button>
          <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 2 }}>
            PITAZO<span style={{ color: C.gold }}>MUNDIAL</span>
          </div>
          <div style={{ width: 60 }} />
        </div>

        {/* Título */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>🏆</div>
          <h1 style={{ fontFamily: "'Oswald',sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            CREA TU <span style={{ color: C.gold }}>GRUPO</span>
          </h1>
          <p style={{ color: '#6B6B7B', fontSize: 13 }}>Elige tu plan e invita a tus amigos</p>
        </div>

        {/* Planes — primero */}
        <div style={{ marginBottom: 20 }}>

          {/* Selector de moneda — PASO 1 (solo web) */}
          {!isNativeAndroid() && <div style={{ marginBottom: 20, background: 'rgba(20,20,31,0.9)', border: `1px solid ${C.border}`, borderRadius: 16, padding: '16px' }}>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
              1. ¿Desde dónde vas a pagar?
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CURRENCIES.map(cur => (
                <button
                  key={cur.code}
                  onClick={() => setCurrency(cur.code)}
                  style={{
                    padding: '6px 13px',
                    background: currency === cur.code
                      ? (cur.provider === 'paypal' ? 'rgba(0,112,224,0.2)' : 'rgba(240,180,41,0.15)')
                      : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${currency === cur.code
                      ? (cur.provider === 'paypal' ? '#0070E0' : C.gold)
                      : C.border}`,
                    borderRadius: 20,
                    color: currency === cur.code
                      ? (cur.provider === 'paypal' ? '#0070E0' : C.gold)
                      : '#6B6B7B',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontFamily: "'Inter',sans-serif",
                    transition: 'all 0.15s',
                  }}
                >
                  {cur.label}
                </button>
              ))}
            </div>
            {isPayPal && (
              <div style={{ fontSize: 10, color: '#0070E0', marginTop: 8 }}>
                🔵 Pagos en USD procesados por PayPal
              </div>
            )}
          </div>}

          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
            2. Selecciona tu plan
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PLANS.filter(p => p.id !== 'corporativo').map(p => {
              const isSelected = selectedPlan === p.id;
              const isFreeP = p.id === 'free';
              const planPrice = PLAN_PRICES[currency]?.[p.id];
              return (
                <div key={p.id} onClick={() => setSelectedPlan(p.id)}
                  style={{
                    background: isSelected ? 'rgba(240,180,41,0.06)' : 'rgba(20,20,31,0.9)',
                    border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? C.gold : C.border}`,
                    borderRadius: 14, padding: '14px 16px', cursor: 'pointer',
                    transition: 'all 0.2s ease', position: 'relative',
                    overflow: 'hidden',
                  }}>

                  {/* Badge popular */}
                  {p.popular && (
                    <div style={{ position: 'absolute', top: 0, right: 0, background: C.gold, color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 9, letterSpacing: 1, padding: '3px 10px', borderRadius: '0 14px 0 10px' }}>
                      POPULAR
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isSelected ? C.gold : '#444'}`, background: isSelected ? C.gold : 'transparent', flexShrink: 0, transition: 'all 0.2s' }} />
                      <div>
                        <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 15, letterSpacing: 1 }}>{p.name}</div>
                        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>
                          Hasta {p.maxMembers} participantes
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, color: isFreeP ? C.green : (isNativeAndroid() ? C.green : (isPayPal ? '#0070E0' : '#F5F5F0')) }}>
                        {isFreeP ? t('free_label') : isNativeAndroid() ? (PLAY_PRICES[p.id] || '') : `${selectedCurrency?.symbol}${planPrice?.toLocaleString('es-CO')}`}
                      </div>
                      {!isFreeP && !isNativeAndroid() && <div style={{ fontSize: 10, color: '#6B6B7B', fontFamily: "'Inter',sans-serif" }}>{currency}</div>}
                    </div>
                  </div>

                  {/* Features (solo si está seleccionado) */}
                  {isSelected && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                      {p.features.map((f, i) => (
                        <div key={i} style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#9999aa', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: C.gold, fontSize: 9 }}>✓</span>{f}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Corporativo */}
            <div style={{ background: 'rgba(124,58,237,0.05)', border: `1px solid rgba(124,58,237,0.2)`, borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 14, letterSpacing: 1, color: '#a78bfa' }}>CORPORATIVO</div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#6B6B7B' }}>+200 participantes · Precio a convenir</div>
              </div>
              <button onClick={() => window.open('mailto:contacto@pitazomundial.com', '_self')}
                style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '7px 12px', fontFamily: "'Oswald',sans-serif", fontSize: 11, color: '#a78bfa', cursor: 'pointer', letterSpacing: 1 }}>
                CONTACTAR
              </button>
            </div>
          </div>
        </div>

        {/* Nombre del grupo — aparece solo cuando hay plan seleccionado */}
        {selectedPlan && (
          <div style={{ background: 'rgba(20,20,31,0.9)', border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px', marginBottom: 20, animation: 'fadeIn 0.3s ease' }}>
            <label style={{ display: 'block', fontSize: 11, color: '#6B6B7B', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
              3. Nombre del grupo
            </label>
            <input
              className="inp"
              value={groupName}
              onChange={e => { setGroupName(e.target.value); setError(''); }}
              placeholder="Ej: La Polla del Trabajo ⚽"
              autoFocus
              style={{ width: '100%', padding: '13px 16px', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.border}`, borderRadius: 12, color: '#F5F5F0', fontSize: 15, fontFamily: "'Inter',sans-serif" }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '14px 16px',
            background: 'rgba(230,57,70,0.12)',
            border: `1px solid rgba(230,57,70,0.4)`,
            borderRadius: 12, color: C.red,
            fontSize: 14, marginBottom: 16, lineHeight: 1.5,
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'shake 0.4s ease',
          }}>
            ⚠️ {error}
          </div>
        )}

        {!created ? (
          <button onClick={handleCreate} disabled={loading}
            style={{ width: '100%', padding: '16px', background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, border: 'none', borderRadius: 14, color: C.bg, fontFamily: "'Oswald',sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: 1, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, boxShadow: '0 4px 15px rgba(240,180,41,0.3)', transition: 'all 0.2s' }}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <span style={{ width: 18, height: 18, border: '3px solid rgba(0,0,0,0.2)', borderTop: `3px solid ${C.bg}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                CREANDO...
              </span>
            ) : isFree ? t('btn_create_group') : isNativeAndroid() ? `⚡ MEJORAR PLAN — ${plan?.name?.toUpperCase()} ${PLAY_PRICES[plan?.id] ? '· ' + PLAY_PRICES[plan?.id] : ''}` : `${isPayPal ? t('btn_pay_paypal') : t('btn_pay_mp')} — ${selectedCurrency?.symbol}${PLAN_PRICES[currency]?.[plan.id]?.toLocaleString('es-CO')} ${currency}`}
          </button>
        ) : (
          <div style={{ textAlign: 'center', background: 'rgba(20,20,31,0.9)', border: `1px solid ${C.border}`, borderRadius: 16, padding: '32px 20px', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 22, letterSpacing: 1, color: C.green, marginBottom: 6 }}>¡GRUPO CREADO!</div>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 16, color: C.gold, marginBottom: 12 }}>{groupName}</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 13, color: '#6B6B7B', marginTop: 8 }}>
              Redirigiendo...
            </div>
          </div>
        )}

        {/* Modal de pago MercadoPago */}
        {showModalPago && (
          <ModalPago
            plan={plan}
            groupId={groupIdCreado}
            userId={currentUser?.uid}
            currency={currency}
            onClose={() => { setShowModalPago(false); navigate('/admin'); }}
            onSuccess={() => {
              setShowModalPago(false);
              setCreated(true);
            }}
          />
        )}
      </div>
    </div>
  );
}